import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Search, CheckCircle2, XCircle, ShieldCheck, ArrowLeft } from "lucide-react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import MarketingHeader from "../components/marketing/MarketingHeader";
import Footer from "../components/marketing/Footer";

const VerifyCertificatePage = () => {
  const { certId: paramCert } = useParams();
  const [certId, setCertId] = useState(paramCert || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  React.useEffect(() => {
    if (paramCert) {
      verify(paramCert);
    }
  }, [paramCert]);

  const verify = async (id) => {
    const target = (id || certId).trim();
    if (!target) return;
    setLoading(true);
    setSearched(true);
    try {
      // Try the safe (slash-replaced) id first
      const safeId = target.replace(/\//g, "-");
      let snap = await getDoc(doc(db, "certificates", safeId));
      if (!snap.exists()) {
        // Fallback: try the raw value as-is
        snap = await getDoc(doc(db, "certificates", target));
      }
      if (snap.exists()) setResult({ found: true, ...snap.data() });
      else setResult({ found: false });
    } catch (e) {
      setResult({ found: false, error: e.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <MarketingHeader />
      <section className="bg-slate-900 py-14 text-white sm:py-20">
        <div className="marketing-container max-w-3xl">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-3">Public Verification</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight md:text-5xl">Verify a Certificate</h1>
          <p className="mt-3 text-base leading-8 text-slate-300">
            Enter the Certificate ID printed on the certificate to confirm authenticity.
          </p>
        </div>
      </section>

      <section className="section-padding">
        <div className="marketing-container max-w-3xl">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm mobile-premium-card sm:p-6">
            <form onSubmit={(e) => { e.preventDefault(); verify(); }} className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  data-testid="verify-cert-input"
                  value={certId}
                  onChange={(e) => setCertId(e.target.value)}
                  placeholder="e.g. CERT1700000000000"
                  className="w-full rounded-xl border border-slate-300 py-3 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>
              <button type="submit" data-testid="verify-cert-btn" disabled={loading} className="rounded-xl bg-blue-600 px-6 py-3 font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
                {loading ? "Verifying..." : "Verify Now"}
              </button>
            </form>

            {searched && !loading && result && (
              <div className="mt-6">
                {result.found ? (
                  <div className="border border-green-200 bg-green-50 rounded-xl p-6" data-testid="verify-result-valid">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center">
                        <CheckCircle2 className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-lg text-green-900">Certificate Verified</h3>
                        <p className="text-xs text-green-700">Status: {result.status || "Valid"}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      {[
                        ["Student Name", result.studentName],
                        ["Course", result.courseTitle],
                        ["Duration", result.duration],
                        ["Issue Date", result.issueDate],
                        ["Certificate ID", result.certificateId],
                        ["Issued By", result.issuedBy],
                      ].map(([k, v]) => (
                        <div key={k}>
                          <div className="text-xs text-slate-500">{k}</div>
                          <div className="font-medium text-slate-900">{v || "—"}</div>
                        </div>
                      ))}
                    </div>
                    {result.certificateUrl && (
                      <a href={result.certificateUrl} target="_blank" rel="noreferrer" data-testid="verify-download-cert-link" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:underline">
                        <ShieldCheck className="w-4 h-4" /> Download Certificate PDF
                      </a>
                    )}
                  </div>
                ) : (
                  <div className="border border-red-200 bg-red-50 rounded-xl p-6" data-testid="verify-result-invalid">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center">
                        <XCircle className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-lg text-red-900">Certificate Not Found</h3>
                        <p className="text-sm text-red-700">We couldn't find a certificate with this ID. Please check and try again.</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <Link to="/" className="mt-8 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default VerifyCertificatePage;
